import {
    useEffect
} from 'react';

import {
    atomFamily,
    useSetRecoilState,
    useRecoilValue
} from 'recoil';

import {
    useCurationConfigValue,
    useSerializeConfigurations,
    useGetFocusedInstanceIdValue,
    CURATION_INSTANCE_ID
} from './widgetConfiguration.js';

function getExportPathObject(allProps, instanceId, focusedInstanceId, format) {

    const isFocused = focusedInstanceId === instanceId;

    const pathProps = {};
    if (isFocused) {
        pathProps.instanceId = instanceId;
    }
    const path = RivalIQ.curation.getBasePath(pathProps);

    const queryParams = _.omit(allProps, 'wProps');
    if (instanceId) {
        _.extend(queryParams, allProps.wProps[instanceId]);
    } else {
        queryParams.wProps = allProps.wProps;
    }
    queryParams.format = format;

    if (!isFocused && instanceId !== CURATION_INSTANCE_ID) {
        queryParams.onlyInstanceId = instanceId;
    }

    return {
        pathname: path,
        query: queryParams
    };
}

// This is based on Slide.getIntroElements and Slide.createIntro.
function createPPTIntroSlide(title, timePeriodStr, appSpecificExportConfig) {

    const Slide = RivalIQ.apps.Reports.models.Slide;
    const focusCompanyName = null;
    const companySetDescriptor = null;
    const landscapeName = RivalIQ.state.view.get('landscapeName');

    const elements = {
        head: title,
        date: timePeriodStr,
        brand: Slide.brandImages.getLarge()
    };

    if (focusCompanyName) {
        elements.focusCompanyTitle = 'Focus Company';
        elements.focusCompany = focusCompanyName;
    }

    if (companySetDescriptor) {
        elements.activeTagTitle = 'Company Set';
        elements.activeTag = RivalIQ.state.utils.getCompanySetName(companySetDescriptor);
        if (RivalIQ.state.utils.isOnlyCompanySet(companySetDescriptor)) {
            delete elements.focusCompany;
            delete elements.focusCompanyTitle;
        }
    }

    if (appSpecificExportConfig) {
        elements.focusCompanyTitle = appSpecificExportConfig?.focusCompanyTitle;
        elements.focusCompany = appSpecificExportConfig?.focusCompany;
    }

    // if this landscape is not named default then use special formatting
    if (landscapeName.toLowerCase() !== 'default') {
        _.extend(elements, {
            landscapeTitle: 'Landscape',
            landscape: _.unescape(landscapeName)
        });
    }

    return new Slide({
        style: Slide.styleTypes.INTRO,
        elements: elements
    });
}

function createSlide(slideConfig, allProps, focusedInstanceId) {
    const elements = {
        brand: RivalIQ.apps.Reports.models.Slide.brandImages.getSmall()
    };

    // currently only used by Charts to show the metric name in the slide footer.
    const descriptions = slideConfig.descriptions;
    if (_.size(descriptions)) {
        _.extend(elements, {
            square: true,
            primaryMetric: descriptions[0].name,
            primaryMetricDescription: descriptions[0].description
        });

        if (descriptions[1]) {
            _.extend(elements, {
                secondaryMetric: descriptions[1].name,
                secondaryMetricDescription: descriptions[1].description
            });
        }
    }

    return new RivalIQ.apps.Reports.models.Slide({
        style: RivalIQ.enums.slideType.SINGLE,
        urls: [
            getExportPathObject(allProps, slideConfig.instanceId, focusedInstanceId, RivalIQ.enums.exportFormat.PPT)
        ],
        elements: elements,
        pdfToPngDpiMultiplier: slideConfig.pdfToPngDpiMultiplier || 1
    });
}

function getPPTSlides(allProps, title, timePeriodStr, instanceId, focusedInstanceId, pptExportConfig, appSpecificExportConfig) {
    let slides = [];
    if (pptExportConfig?.showIntroSlide) {
        slides.push(createPPTIntroSlide(title, timePeriodStr, appSpecificExportConfig));
    }

    let slideConfigs = pptExportConfig?.slideConfigs;
    if (_.isEmpty(slideConfigs)) {
        if (instanceId) {
            slideConfigs = [{
                instanceId
            }];
        } else {
            throw new Error(`No slides defined for PPT export`);
        }
    }

    slides = slides.concat(_.map(slideConfigs, slideConfig => createSlide(slideConfig, allProps, focusedInstanceId)));

    return slides;
}

function makeBridgingDashboardModel(instanceId, focusedInstanceId, allProps, exportConfig) {

    const {title, timePeriodStr, supportedExportFormats, pptExportConfig, pdfExportConfig, csvExportConfig, appSpecificExportConfig} = exportConfig;

    const instanceProps = allProps.wProps?.[instanceId] || {};

    const curationId = RivalIQ.curation.id;
    const exportTrackingId = instanceId ? `exported_${curationId}_details_${instanceId}` : `exported_${curationId}`;
    const scheduledExportTrackingId = instanceId ? `scheduled_exported_${curationId}_details_${instanceId}` : `scheduled_export_${curationId}`;

    const getProp = propKey => (_.isUndefined(instanceProps[propKey]) ? allProps[propKey] : instanceProps[propKey]);

    const filename = RivalIQ.export.filename.base({
        title: title,
        landscapeName: appSpecificExportConfig?.landscapeName,
        appName: appSpecificExportConfig?.appName,
        companyName: appSpecificExportConfig?.focusCompany
    });

    const exportTitle = appSpecificExportConfig?.exportTitle ?? title;
    const dashboardModel = {
        // PDF methods
        getExportOrientation: () => pdfExportConfig?.exportOrientation || RivalIQ.enums.orientation.PORTRAIT,
        getExportWidth: () => pdfExportConfig?.exportWidth || 1450,
        getUsePdfPostMargin: () => (_.isUndefined(pdfExportConfig?.usePdfPostMargin) ? false : pdfExportConfig?.usePdfPostMargin),

        // PPT methods
        getExportStyles: function () {
            const branding = RivalIQ.state.view.getActiveBranding();
            return RivalIQ.utils.branding.overrideReportStyles(branding);
        },
        getSlides: function () {

            return getPPTSlides(allProps, title, timePeriodStr, instanceId, focusedInstanceId, pptExportConfig, appSpecificExportConfig);
        },

        // CSV methods
        getPropsForFormat: format => {
            if (format !== 'csv') {
                throw new Error(`only CSV formats supported`);
            }

            return csvExportConfig;
        },

        // email methods
        getTitle: _.constant(exportTitle),
        getExportFormats: _.constant(supportedExportFormats),

        // schedule methods
        id: instanceId,
        buildScheduledReport: (attrs) => {
            return RivalIQ.apps.Dashboard.mixins.Export.buildScheduledReport.call(dashboardModel, attrs);
        },
        isTimePeriodDisabled: () => {
            return _.includes([
                false,
                RivalIQ.enums.timePeriod.type.TODAY,
                RivalIQ.enums.timePeriod.type.NOT_AVAILABLE
            ], RivalIQ.curation.timePeriod?.id);
        },
        getBasePath: () => {
            const isFocused = focusedInstanceId === instanceId;

            const pathProps = {};
            if (isFocused || (instanceId && instanceId !== CURATION_INSTANCE_ID)) {
                pathProps.instanceId = instanceId;
            }
            const path = RivalIQ.curation.getBasePath(pathProps);
            return path;
        },
        getPropsForSchedule: function () {
            return allProps;
        },
        getScheduledExportTracking: function (format) {
            return {
                name: scheduledExportTrackingId,
                category: 'export',
                options: _.defaults({
                    format
                }, allProps)
            };
        },

        // General methods
        getProp: getProp,
        getProps: _.constant(allProps),

        getReportAttributes: function () {
            return {
                // These two strings are used in report center.
                title: title,
                timePeriodName: timePeriodStr || null,

                filename: filename,
                hideFromDownloads: false,

                focusCompanyId: getProp('focusCompanyId'),
                activeSources: getProp('activeSources')
            };
        },

        getExportPathObject: format => getExportPathObject(allProps, instanceId, focusedInstanceId, format),

        getExportTracking: function () {
            return {
                name: exportTrackingId,
                category: 'export',
                options: allProps
            };
        }
    };

    return dashboardModel;
}

// This internal version of useBridgingDashboardModel necessary for calling this hook from within a hook that interacts
// with the recoil atom for exportConfigs.
function useBridgingDashboardModel_internal(instanceId, exportConfig, cb, dependencies) {
    const {title, timePeriodStr, pptExportConfig, pdfExportConfig, csvExportConfig} = exportConfig;

    const landscapeId = useCurationConfigValue(instanceId, 'landscapeId');
    const email = useCurationConfigValue(instanceId, 'email');

    const focusedInstanceId = useGetFocusedInstanceIdValue();

    return useSerializeConfigurations(allProps => {
        allProps = _.extend({
            landscapeId: landscapeId,
            email: email,
            exportWidthClass: 'react-curation'
        }, allProps);

        // It's extremely important that we delete the scheduleId from the props; otherwise, when we render the export
        // we'll end up scheduling more exports.
        // This list of props is duplicated from export.js.
        const ignoreList = ['triggerExport', 'scheduleId', 'isSchedule'];
        allProps = _.omit(allProps, ignoreList);

        const dashboardModel = makeBridgingDashboardModel(instanceId, focusedInstanceId, allProps, exportConfig);

        cb(dashboardModel);
    }, [instanceId, title, timePeriodStr, focusedInstanceId, pptExportConfig, pdfExportConfig, csvExportConfig].concat(dependencies || []));
}

export function useBridgingDashboardModel(instanceId, cb, dependencies) {

    const exportConfig = useGetExportConfig(instanceId) || {};
    return useBridgingDashboardModel_internal(instanceId, exportConfig, cb, dependencies);
}


const exportConfigByInstanceId = atomFamily({
    key: 'exportConfigByInstanceId',
    default: _.constant({})
});

export function useGetExportConfig(instanceId) {
    instanceId ||= CURATION_INSTANCE_ID;

    return useRecoilValue(exportConfigByInstanceId(instanceId));
}

// Hook to register export config for a widget. This info will be used for both generating exports at user request as well
// as generation of scheduled exports.
// pdfExportConfig can include exportOrientation, exportWidth, and usePdfPostMargin.
// See createSlide for details of what can be included in pptExportConfig.
// csvExportConfig should be an object that will be passed as the csv export config. It should at least contain
export function useSetExportConfig(instanceId, exportConfig) {
    if (arguments.length === 1) {
        exportConfig = instanceId;
        instanceId = CURATION_INSTANCE_ID;
    }

    const focusedInstanceId = useGetFocusedInstanceIdValue() || CURATION_INSTANCE_ID;
    const scheduleId = useCurationConfigValue('scheduleId');

    const scheduleReportFn = useBridgingDashboardModel_internal(instanceId, exportConfig, dashboardModel => {
        RivalIQ.curation._setBridgeDashboardModel(dashboardModel);
    });

    const updateStatus = useSetRecoilState(exportConfigByInstanceId(instanceId));
    useEffect(() => {
        updateStatus(exportConfig);

        // If we have a scheduleId, we need to do this dance to setup the dashboard model on the reactCurationBridge.
        if (scheduleId && (focusedInstanceId === instanceId || (!focusedInstanceId && !instanceId))) {
            scheduleReportFn();
        }
    });
}
